// src/components/OrderList/OrderList.jsx
import React, { useState, useEffect } from 'react';
import { RiArchiveLine, RiDownloadLine, RiSearchLine, RiFilterLine, RiArrowLeftLine, RiArrowRightLine } from 'react-icons/ri';
import { Link } from 'react-router-dom'; // for View button navigation
import './OrdersList.css';
import { BASE_URL } from '../utils/constants';


const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;

  // Fetch real orders from backend
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/api/orders`);
        if (!response.ok) {
          throw new Error(`Failed to fetch orders: ${response.status}`);
        }

        const data = await response.json();
        setOrders(data);
        setFilteredOrders(data);
      } catch (err) {
        console.error('Orders fetch error:', err);
        setError('Unable to load orders. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Search/filter logic
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results = orders.filter(order => 
      order.reference?.toLowerCase().includes(term) ||
      order.phone?.toLowerCase().includes(term) ||
      order.accountName?.toLowerCase().includes(term) ||
      order.items.some(item => item.name?.toLowerCase().includes(term))
    );

    setFilteredOrders(results);
    setCurrentPage(1); // Reset to first page on search
  }, [searchTerm, orders]);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  const getStatusClass = (paymentStatus, deliveryStatus) => {
    if (paymentStatus === 'failed') return 'status-failed';
    if (deliveryStatus === 'delivered') return 'status-delivered';
    if (deliveryStatus === 'shipped' || deliveryStatus === 'out for delivery') return 'status-out';
    if (deliveryStatus === 'in transit') return 'status-transit';
    if (deliveryStatus === 'pending' || paymentStatus === 'pending') return 'status-pending';
    return 'status-processing';
  };

  const getStatusText = (payment, delivery) => {
    if (payment === 'failed') return 'Payment Failed';
    if (delivery === 'delivered') return 'Delivered';
    if (delivery === 'shipped') return 'Shipped';
    if (delivery === 'processing') return 'Processing';
    return delivery.charAt(0).toUpperCase() + delivery.slice(1) || 'Pending';
  };

  if (loading) return <div className="loading">Loading orders...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="orders-list-page">
      <header className="admin-header">
        <div className="logo-section">
          <div className="logo-icon">
            <RiArchiveLine size={24} color="white" />
          </div>
          <span className="logo-text">OrderFlow</span>
        </div>

        <nav className="admin-nav">
          <Link to="/dashboard">Dashboard</Link>
          <Link to="/orders" className="active">Orders</Link>
          <Link to="/profile">Profile</Link>
        </nav>

        <div className="header-actions">
          <button className="action-btn">
            <RiDownloadLine size={18} /> Export
          </button>
        </div>
      </header>

      <main className="orders-main">
        <div className="filters-bar">
          <div className="search-container">
            <RiSearchLine className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search orders by ID, phone, customer or item..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <button className="filter-btn">
            <RiFilterLine size={18} /> Filters
          </button>
        </div>

        <div className="table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer / Phone</th>
                <th>Date</th>
                <th>Status</th>
                <th>Items</th>
                <th>Amount (ZMK)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                    No orders found {searchTerm ? `matching "${searchTerm}"` : ''}
                  </td>
                </tr>
              ) : (
                paginatedOrders.map(order => (
                  <tr key={order.reference}>
                    <td className="order-id">{order.reference}</td>
                    <td>
                      {order.accountName || 'Unknown'}<br />
                      <small>{order.phone}</small>
                    </td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(order.paymentStatus, order.deliveryStatus)}`}>
                        {getStatusText(order.paymentStatus, order.deliveryStatus)}
                      </span>
                    </td>
                    <td>{order.items.length}</td>
                    <td className="amount">
                      ZMK {order.items.reduce((sum, item) => sum + item.totalCost, 0).toFixed(2)}
                    </td>
                    <td>
                      <Link to={`/dashboard/order/${order.reference}`} className="view-btn">
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredOrders.length > 0 && (
          <div className="pagination-bar">
            <div className="pagination-info">
              Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
            </div>

            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              >
                <RiArrowLeftLine />
              </button>

              <span className="page-number active">{currentPage}</span>

              <button
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              >
                <RiArrowRightLine />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default OrdersList;
// import React, { useState } from 'react';
// import {
//   RiArchiveLine,          // from previous fix (logo)
//   RiDownloadLine,
//   RiSearchLine,
//   RiFilterLine,
//   RiArrowLeftLine,        // ← replaced RiChevronLeftLine (does not exist)
//   RiArrowRightLine        // ← replaced RiChevronRightLine (does not exist)
// } from 'react-icons/ri';

// import './OrdersList.css';

// const mockOrders = [
//   {
//     id: 'ORD-2024-001842',
//     customer: 'Robert Kim',
//     date: 'Dec 17, 2024',
//     status: 'Out for Delivery',
//     amount: '$142.50',
//     items: 4,
//     priority: 'Normal',
//   },
//   {
//     id: 'ORD-2024-001823',
//     customer: 'Sophia Martinez',
//     date: 'Dec 16, 2024',
//     status: 'Delivered',
//     amount: '$89.50',
//     items: 2,
//     priority: 'Express',
//   },
//   {
//     id: 'ORD-2024-001815',
//     customer: 'James Patel',
//     date: 'Dec 16, 2024',
//     status: 'In Transit',
//     amount: '$219.99',
//     items: 5,
//     priority: 'Normal',
//   },
//   {
//     id: 'ORD-2024-001799',
//     customer: 'Anna Thompson',
//     date: 'Dec 15, 2024',
//     status: 'Pending Pickup',
//     amount: '$67.80',
//     items: 3,
//     priority: 'Urgent',
//   },
//   {
//     id: 'ORD-2024-001784',
//     customer: 'Maria Gonzalez',
//     date: 'Dec 15, 2024',
//     status: 'Processing',
//     amount: '$134.20',
//     items: 6,
//     priority: 'Normal',
//   },
//   // ... you can add more rows for testing pagination
// ];

// const OrdersList = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 10;
//   const totalPages = Math.ceil(mockOrders.length / itemsPerPage);

//   const getStatusClass = (status) => {
//     if (status === 'Delivered') return 'status-delivered';
//     if (status === 'Out for Delivery') return 'status-out';
//     if (status === 'In Transit') return 'status-transit';
//     if (status === 'Pending Pickup') return 'status-pending';
//     return 'status-processing';
//   };

//   const getPriorityClass = (priority) => {
//     if (priority === 'Urgent') return 'priority-urgent';
//     if (priority === 'Express') return 'priority-express';
//     return 'priority-normal';
//   };

//   return (
//     <div className="orders-list-page">
//       <header className="admin-header">
//         <div className="logo-section">
//           <div className="logo-icon">
//             <RiArchiveLine size={24} color="white" />
//           </div>
//           <span className="logo-text">OrderFlow</span>
//         </div>

//         <nav className="admin-nav">
//           <a href="#dashboard">Dashboard</a>
//           <a href="#orders" className="active">Orders</a>
//           <a href="#profile">Profile</a>
//         </nav>

//         <div className="header-actions">
//           <button className="action-btn">
//             <RiDownloadLine size={18} /> Export
//           </button>
//         </div>
//       </header>

//       <main className="orders-main">
//         <div className="filters-bar">
//           <div className="search-container">
//             <RiSearchLine className="search-icon" size={18} />
//             <input type="text" placeholder="Search orders by ID, customer or SKU..." />
//           </div>

//           <button className="filter-btn">
//             <RiFilterLine size={18} /> Filters
//           </button>
//         </div>

//         <div className="table-container">
//           <table className="orders-table">
//             <thead>
//               <tr>
//                 <th>Order ID</th>
//                 <th>Customer</th>
//                 <th>Date</th>
//                 <th>Status</th>
//                 <th>Items</th>
//                 <th>Amount</th>
//                 <th>Priority</th>
//                 <th>Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {mockOrders.map((order) => (
//                 <tr key={order.id}>
//                   <td className="order-id">{order.id}</td>
//                   <td>{order.customer}</td>
//                   <td>{order.date}</td>
//                   <td>
//                     <span className={`status-badge ${getStatusClass(order.status)}`}>
//                       {order.status}
//                     </span>
//                   </td>
//                   <td>{order.items}</td>
//                   <td className="amount">${order.amount}</td>
//                   <td>
//                     <span className={`priority-badge ${getPriorityClass(order.priority)}`}>
//                       {order.priority}
//                     </span>
//                   </td>
//                   <td>
//                     <button className="view-btn">View</button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         <div className="pagination-bar">
//           <div className="pagination-info">
//             Showing 1–{Math.min(itemsPerPage, mockOrders.length)} of {mockOrders.length} orders
//           </div>

//           <div className="pagination-controls">
//             <button
//               className="page-btn"
//               disabled={currentPage === 1}
//               onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
//             >
//               <RiArrowLeftLine />    {/* ← updated here */}
//             </button>

//             <span className="page-number active">{currentPage}</span>

//             <button
//               className="page-btn"
//               disabled={currentPage === totalPages}
//               onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
//             >
//               <RiArrowRightLine />   {/* ← updated here */}
//             </button>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default OrdersList;

// import React, { useState } from 'react';
// //import { RiPackageLine } from "react-icons/ri";
// import {
//   RiPackageLine,
//   RiDownloadLine,
//   RiSearchLine,
//   RiFilterLine,
//   RiChevronLeftLine,
//   RiChevronRightLine,
// } from "react-icons/ri";
// //import { RiSearchLine, RiFilterLine, RiDownloadLine, RiChevronLeftLine, RiChevronRightLine } from 'react-icons/ri';
// import './OrdersList.css';

// const mockOrders = [
//   {
//     id: 'ORD-2024-001842',
//     customer: 'Robert Kim',
//     date: 'Dec 17, 2024',
//     status: 'Out for Delivery',
//     amount: '$142.50',
//     items: 4,
//     priority: 'Normal',
//   },
//   {
//     id: 'ORD-2024-001823',
//     customer: 'Sophia Martinez',
//     date: 'Dec 16, 2024',
//     status: 'Delivered',
//     amount: '$89.50',
//     items: 2,
//     priority: 'Express',
//   },
//   {
//     id: 'ORD-2024-001815',
//     customer: 'James Patel',
//     date: 'Dec 16, 2024',
//     status: 'In Transit',
//     amount: '$219.99',
//     items: 5,
//     priority: 'Normal',
//   },
//   {
//     id: 'ORD-2024-001799',
//     customer: 'Anna Thompson',
//     date: 'Dec 15, 2024',
//     status: 'Pending Pickup',
//     amount: '$67.80',
//     items: 3,
//     priority: 'Urgent',
//   },
//   {
//     id: 'ORD-2024-001784',
//     customer: 'Maria Gonzalez',
//     date: 'Dec 15, 2024',
//     status: 'Processing',
//     amount: '$134.20',
//     items: 6,
//     priority: 'Normal',
//   },
//   // ... you can add more rows for testing pagination
// ];

// const OrdersList = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 10;
//   const totalPages = Math.ceil(mockOrders.length / itemsPerPage);

//   const getStatusClass = (status) => {
//     if (status === 'Delivered') return 'status-delivered';
//     if (status === 'Out for Delivery') return 'status-out';
//     if (status === 'In Transit') return 'status-transit';
//     if (status === 'Pending Pickup') return 'status-pending';
//     return 'status-processing';
//   };

//   const getPriorityClass = (priority) => {
//     if (priority === 'Urgent') return 'priority-urgent';
//     if (priority === 'Express') return 'priority-express';
//     return 'priority-normal';
//   };

//   return (
//     <div className="orders-list-page">
//       <header className="admin-header">
//         <div className="logo-section">
//           <div className="logo-icon">
//             <RiPackageLine size={24} color="white" />
//           </div>
//           <span className="logo-text">OrderFlow</span>
//         </div>

//         <nav className="admin-nav">
//           <a href="#dashboard">Dashboard</a>
//           <a href="#orders" className="active">Orders</a>
//           <a href="#profile">Profile</a>
//         </nav>

//         <div className="header-actions">
//           <button className="action-btn">
//             <RiDownloadLine size={18} /> Export
//           </button>
//         </div>
//       </header>

//       <main className="orders-main">
//         <div className="filters-bar">
//           <div className="search-container">
//             <RiSearchLine className="search-icon" size={18} />
//             <input type="text" placeholder="Search orders by ID, customer or SKU..." />
//           </div>

//           <button className="filter-btn">
//             <RiFilterLine size={18} /> Filters
//           </button>
//         </div>

//         <div className="table-container">
//           <table className="orders-table">
//             <thead>
//               <tr>
//                 <th>Order ID</th>
//                 <th>Customer</th>
//                 <th>Date</th>
//                 <th>Status</th>
//                 <th>Items</th>
//                 <th>Amount</th>
//                 <th>Priority</th>
//                 <th>Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {mockOrders.map((order) => (
//                 <tr key={order.id}>
//                   <td className="order-id">{order.id}</td>
//                   <td>{order.customer}</td>
//                   <td>{order.date}</td>
//                   <td>
//                     <span className={`status-badge ${getStatusClass(order.status)}`}>
//                       {order.status}
//                     </span>
//                   </td>
//                   <td>{order.items}</td>
//                   <td className="amount">${order.amount}</td>
//                   <td>
//                     <span className={`priority-badge ${getPriorityClass(order.priority)}`}>
//                       {order.priority}
//                     </span>
//                   </td>
//                   <td>
//                     <button className="view-btn">View</button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         <div className="pagination-bar">
//           <div className="pagination-info">
//             Showing 1–{Math.min(itemsPerPage, mockOrders.length)} of {mockOrders.length} orders
//           </div>

//           <div className="pagination-controls">
//             <button
//               className="page-btn"
//               disabled={currentPage === 1}
//               onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
//             >
//               <RiChevronLeftLine />
//             </button>

//             <span className="page-number active">{currentPage}</span>

//             <button
//               className="page-btn"
//               disabled={currentPage === totalPages}
//               onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
//             >
//               <RiChevronRightLine />
//             </button>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default OrdersList;